import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_STYLES = {
  active: { bg: "rgba(212,175,55,0.12)", color: "#D4AF37", label: "Active" },
  trialing: { bg: "rgba(212,175,55,0.12)", color: "#D4AF37", label: "Trialing" },
  past_due: { bg: "rgba(255,165,0,0.12)", color: "#FFA500", label: "Past due" },
  unpaid: { bg: "rgba(255,99,71,0.12)", color: "#FF6347", label: "Unpaid" },
  canceled: { bg: "rgba(255,99,71,0.12)", color: "#FF6347", label: "Canceled" },
  incomplete: { bg: "rgba(160,160,160,0.12)", color: "#aaa", label: "Incomplete" },
  incomplete_expired: { bg: "rgba(255,99,71,0.12)", color: "#FF6347", label: "Expired" },
  paused: { bg: "rgba(160,160,160,0.12)", color: "#aaa", label: "Paused" },
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return null; }
}

export default function SubscriptionCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    api.get("/billing/me")
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="shimmer" data-testid="sub-card-loading" style={{ height: 96, borderRadius: 14, marginBottom: 22 }} />;
  }
  if (!info) return null;

  // Hide entirely for bypass accounts — they aren't billed.
  if (info.is_bypass) {
    return (
      <div data-testid="sub-card-bypass" className="bg-card" style={{ padding: 16, marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 4 }}>SUBSCRIPTION</div>
          <div className="serif" style={{ fontSize: 18, color: "#fff" }}>Complimentary access</div>
          <div className="muted" style={{ fontSize: 12 }}>No billing on this account.</div>
        </div>
      </div>
    );
  }

  const statusKey = (info.subscription_status || "incomplete").toLowerCase();
  const s = STATUS_STYLES[statusKey] || STATUS_STYLES.incomplete;
  const renewal = formatDate(info.current_period_end);
  const planLabel = info.plan === "annual" ? "Annual ($99/yr)" : info.plan === "monthly" ? "Monthly ($10/mo)" : "—";

  const openPortal = async () => {
    setOpening(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/dashboard" });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not open billing portal.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not open billing portal.";
      toast.error(msg);
    } finally {
      setOpening(false);
    }
  };

  return (
    <div data-testid="sub-card" className="bg-card" style={{ padding: 18, marginBottom: 22, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: "0.3em", color: "var(--gold)", marginBottom: 6 }}>SUBSCRIPTION</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span data-testid="sub-status-badge" style={{ background: s.bg, color: s.color, padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: "0.05em" }}>
            {s.label}
          </span>
          <span className="serif" style={{ fontSize: 18, color: "#fff" }}>{planLabel}</span>
        </div>
        <div className="muted" data-testid="sub-renewal" style={{ fontSize: 13, marginTop: 8 }}>
          {info.cancel_at_period_end && renewal ? (
            <>Access ends on <span style={{ color: "#fff" }}>{renewal}</span></>
          ) : renewal ? (
            <>Renews on <span style={{ color: "#fff" }}>{renewal}</span></>
          ) : statusKey === "canceled" ? (
            "Your subscription is canceled."
          ) : (
            "No active renewal scheduled."
          )}
        </div>
      </div>
      <button
        data-testid="manage-subscription-btn"
        onClick={openPortal}
        disabled={opening}
        className="btn-ghost"
        style={{ cursor: opening ? "wait" : "pointer", opacity: opening ? 0.7 : 1 }}
      >
        {opening ? "Opening…" : "Manage Subscription"}
      </button>
    </div>
  );
}
